/* eslint-disable unused-imports/no-unused-vars */
/* eslint-disable no-param-reassign */
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  ViewFormsState,
  ISaveFormModalForm,
  ModalStates,
  IFilteredForms,
  IFormFilterPayload,
  IFormDocument,
  IDeleteForm,
  IOpenModal,
  ISubmittedFormResponse,
  FormTypes,
  FormBuilderVersion,
} from './interface';

export const initialState: ViewFormsState = {
  filteredForms: { result: [], total: 0 },
  loading: false,
  formSettings: {
    formTitle: '',
    formDescription: '',
    fileName: '',
    scormFileUrl: '',
    formType: FormTypes.INTERACTIVE_FORM,
    marksEnabled: true,
    displayMarksUntilMarkingComplete: false,
    passMark: 0,
    badgeImgUrl: '',
    badgeDescription: '',
    version: FormBuilderVersion.V2,
    badgeTitle: '',
  },
  stateLoading: false,
  formId: '',
  formConfig: '',
  answerData: '',
  version: FormBuilderVersion.V2, // default to v2
  status: '',
  userSessionId: '',
  directChatUserSessionId: '',
  userModuleCompletionId: '',
  modal: ModalStates.close,
  createStatus: 0,
  creationRedirectFrom: '',
  createdAt: '',
  updatedAt: '',
  formConfigModal: ModalStates.close,
  chatIdForSingleChatFormShare: '',
};

export const viewFormsSlice = createSlice({
  name: 'feature/forms',
  initialState,
  reducers: {
    getFilteredForms(state: ViewFormsState, _action: PayloadAction<IFormFilterPayload>) {
      state.stateLoading = true;
    },
    getFilteredFormsSucceeded(state: ViewFormsState, action: PayloadAction<IFilteredForms>) {
      state.filteredForms = action.payload;
      state.stateLoading = false;
    },
    getFilteredFormsFailed(state: ViewFormsState) {
      state.stateLoading = false;
    },
    clearFilteredForms(state: ViewFormsState) {
      state.filteredForms = { result: [], total: 0 };
    },
    setFormId(state: ViewFormsState, action: PayloadAction<string>) {
      state.formId = action.payload;
    },
    setUserSessionId(state: ViewFormsState, action: PayloadAction<string>) {
      state.userSessionId = action.payload;
    },
    setDirectChatUserSessionId(state: ViewFormsState, action: PayloadAction<string>) {
      state.directChatUserSessionId = action.payload;
    },
    setUserModuleCompletionId(state: ViewFormsState, action: PayloadAction<string>) {
      state.userModuleCompletionId = action.payload;
    },
    getForm(state: ViewFormsState) {
      state.stateLoading = true;
    },
    getFormSucceeded(state: ViewFormsState, action: PayloadAction<IFormDocument>) {
      const form = action.payload;
      if (form) {
        state.formSettings = {
          formTitle: form.formTitle,
          formDescription: form.formDescription,
          fileName: form.fileName,
          scormFileUrl: form.scormFileUrl,
          formType: form.formType || FormTypes.INTERACTIVE_FORM,
          marksEnabled: form.marksEnabled,
          displayMarksUntilMarkingComplete: form.displayMarksUntilMarkingComplete,
          passMark: form.passMark,
          badgeImgUrl: form.badgeImgUrl,
          badgeDescription: form.badgeDescription,
          badgeTitle: form.badgeTitle,
          version: form.version,
        };
        state.formConfig = form.formConfig;
        state.answerData = form.answerData;
        state.stateLoading = false;
      } else {
        state.stateLoading = false;
        state = initialState;
      }
    },
    getFormFailed(state: ViewFormsState) {
      state.stateLoading = false;
    },
    getSubmittedForm(state: ViewFormsState) {
      state.loading = true;
    },
    getSubmittedFormSucceeded(
      state: ViewFormsState,
      action: PayloadAction<ISubmittedFormResponse>,
    ) {
      if (action.payload) {
        state.formSettings = {
          formTitle: action.payload.form?.formTitle,
          formDescription: action.payload.form?.formDescription,
          fileName: action.payload.form?.fileName,
          scormFileUrl: action.payload.form?.scormFileUrl,
          formType: action.payload.form?.formType ?? FormTypes.INTERACTIVE_FORM,
          marksEnabled: action.payload.form.marksEnabled,
          displayMarksUntilMarkingComplete: action.payload.form.displayMarksUntilMarkingComplete,
          passMark: action.payload.form.passMark,
          badgeImgUrl: action.payload.form.badgeImgUrl,
          version: action.payload.form.version,
          badgeDescription: action.payload.form.badgeDescription,
          badgeTitle: action.payload.form.badgeTitle,
        };
        state.formConfig = action.payload.data;
        state.answerData = action.payload.form.answerData;
        state.status = action.payload.status;
        state.createdAt = action.payload.createdAt;
        state.updatedAt = action.payload.updatedAt;
        state.loading = false;
      } else {
        state.loading = false;
        state = initialState;
      }
    },
    getSubmittedFormFailed(state: ViewFormsState) {
      state.loading = false;
      state = initialState;
    },
    openConfirmationModal(state: ViewFormsState, action: PayloadAction<IOpenModal>) {
      const temp = state.filteredForms.result;
      temp.forEach((form) => {
        if (form._id === action.payload.id) {
          form.modalState = action.payload.state;
        }
      });
      state.filteredForms.result = [...temp];
    },
    closeConfirmationModal(state: ViewFormsState, action: PayloadAction<string>) {
      const temp = state.filteredForms.result;
      temp.forEach((form) => {
        if (form._id === action.payload) {
          form.modalState = ModalStates.close;
        }
      });
      state.filteredForms.result = [...temp];
    },

    openModal(state: ViewFormsState) {
      state.modal = ModalStates.open;
    },
    closeModal(state: ViewFormsState) {
      state.modal = ModalStates.close;
    },
    setFormSetting(state: ViewFormsState, action: PayloadAction<ISaveFormModalForm>) {
      state.formSettings = action.payload;
      state.createStatus = 1;
      state.modal = ModalStates.close;
    },
    setFormConfig(state: ViewFormsState, action: PayloadAction<string>) {
      state.formConfig = action.payload;
    },
    setFormVersion(state: ViewFormsState, action: PayloadAction<FormBuilderVersion>) {
      state.version = action.payload;
    },
    setAnswerData(state: ViewFormsState, action: PayloadAction<string>) {
      state.answerData = action.payload;
    },
    createForm(state: ViewFormsState, _action: PayloadAction<{ resolve: any; reject: any }>) {
      state.loading = true;
    },
    createFormSucceeded(state: ViewFormsState) {
      state.stateLoading = false;
      state.loading = false;
    },
    createFormFailed(state: ViewFormsState) {
      state.stateLoading = false;
      state.loading = false;
    },
    saveScormForm(state: ViewFormsState, _action: PayloadAction<ISaveFormModalForm>) {
      state.stateLoading = true;
      state.loading = true;
    },
    saveScormFormSucceeded(state: ViewFormsState) {
      state.stateLoading = false;
      state.loading = false;
    },
    saveScormFormFailed(state: ViewFormsState) {
      state.stateLoading = false;
      state.loading = false;
    },
    editForm(state: ViewFormsState, _action: PayloadAction<{ resolve: any; reject: any }>) {
      state.loading = true;
    },
    editFormSucceeded(state: ViewFormsState) {
      state.stateLoading = false;
      state.loading = false;
    },
    editFormFailed(state: ViewFormsState) {
      state.stateLoading = false;
      state.loading = false;
    },
    markSubmitForms(state: ViewFormsState) {
      state.loading = true;
    },
    markSubmitFormsSucceeded(state: ViewFormsState) {
      state.loading = false;
    },
    markSubmitFormsFailed(state: ViewFormsState) {
      state.loading = false;
    },
    deleteForm(state: ViewFormsState, _action: PayloadAction<IDeleteForm>) {
      state.loading = true;
    },
    deleteFormSucceeded(state: ViewFormsState, action: PayloadAction<string>) {
      state.modal = ModalStates.close;
      state.loading = false;
    },
    deleteFormFailed(state: ViewFormsState) {
      state.loading = false;
    },
    closeModalConfirmation(state: ViewFormsState, action: PayloadAction<string>) {
      const temp = state.filteredForms.result;
      temp.forEach((form) => {
        if (form._id === action.payload) {
          form.modalState = ModalStates.close;
        }
      });
      state.filteredForms.result = [...temp];
    },
    setCreationRedirectFrom(state: ViewFormsState, action: PayloadAction<string>) {
      state.creationRedirectFrom = action.payload;
    },
    reset(state: ViewFormsState) {
      state.formSettings = initialState.formSettings;
      state.loading = false;
      state.formId = initialState.formId;
      state.createStatus = initialState.createStatus;
      state.formConfig = initialState.formConfig;
    },
    openFormConfigModal(state: ViewFormsState) {
      state.formConfigModal = ModalStates.open;
    },
    closeFormConfigModal(state: ViewFormsState) {
      state.formConfigModal = ModalStates.close;
    },
    startLoading(state: ViewFormsState) {
      state.loading = true;
    },
    stopLoading(state: ViewFormsState) {
      state.loading = false;
    },
    setChatIdForSingleChatFormShare(state: ViewFormsState, action: PayloadAction<string>) {
      state.chatIdForSingleChatFormShare = action.payload;
    },
  },
});

export const { actions: viewFormsActions } = viewFormsSlice;
